import React, { memo, useMemo } from 'react';
import { Pressable, Platform } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { View } from '@constants/Themed';
import parseByDelimiter from '@utils/parseByDelimiter';
import returnIcon from '../utils/returnIcon';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import toTitleCase from '@hooks/toTitleCase';
import { store } from '@reduxLocal/persistState';
import { HIDE_NOTIFICATIONS_MODAL, SHOW_NOTIFICATIONS_PANEL } from '@reduxLocal/actions/types';
import { YStack, Text, SizableStack, SizableText, Separator } from 'tamagui';
import { useNavigation } from '@react-navigation/native';
import Button from '@components/UI/Button/Button';
import { useDispatch } from 'react-redux';
import getDaysAgo from '@utils/getDaysAgo';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import LottieView from '@utils/lottieAnimation';

/**
 * Generates a context-sensitive notification message based on the notification data
 */
const getContextualMessage = (item: any): string => {
  // If there's already a title, use it (unless it's a generic one)
  if (item.title && !item.title.includes('Detected') && !item.title.includes('event in')) {
    return  parseByDelimiter(item.title);
  }

  const operationType = item.operationType?.toLowerCase();
  const collection = item.ns?.coll || item.collection;
  const fullDocument = item.fullDocument;
  const documentKey = item.documentKey;
  const postedBy = item.postedBy;
  const dataTitle = item.dataTitle || fullDocument?.dataTitle || fullDocument?.title;
  const notificationType = item.type?.toLowerCase() || '';

  // Extract meaningful fields from fullDocument
  const getDocumentName = () => {
    if (dataTitle) return parseByDelimiter(dataTitle);
    if (fullDocument?.name) return parseByDelimiter(fullDocument.name);
    if (fullDocument?.title) return parseByDelimiter(fullDocument.title);
    if (fullDocument?.dataTitle) return parseByDelimiter(fullDocument.dataTitle);
    if (fullDocument?.email) return fullDocument.email;
    if (fullDocument?.company_name) return parseByDelimiter(fullDocument.company_name);
    return null;
  };

  const documentName = getDocumentName();
  const collectionName = collection ? parseByDelimiter(collection) : null;
  const userName = postedBy
    ? `${toTitleCase(postedBy.profile_information?.name || '')} ${toTitleCase(postedBy.profile_information?.surname || '')}`.trim()
    : null;

  // Handle error notifications
  if (item.error || notificationType.includes('error') || notificationType.includes('failed')) {
    const errorMessage = item.error || item.message || item.body;
    if (documentName) {
      return `${documentName} - ${errorMessage || 'Operation failed'}`;
    }
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} - ${errorMessage || 'Job failed'}`;
    }
    if (errorMessage && !errorMessage.includes('Detected') && !errorMessage.includes('in ') && !errorMessage.includes('event in')) {
      return errorMessage;
    }
    return 'An error occurred';
  }

  // Handle warning notifications
  if (item.warning || notificationType.includes('warning') || notificationType.includes('warn')) {
    const warningMessage = item.warning || item.message || item.body;
    if (documentName) {
      return `${documentName} - ${warningMessage || 'Please review'}`;
    }
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} - ${warningMessage || 'Action required'}`;
    }
    if (warningMessage && !warningMessage.includes('Detected') && !warningMessage.includes('in ') && !warningMessage.includes('event in')) {
      return warningMessage;
    }
    return 'Please review';
  }

  // If there's a meaningful message (not generic), use it
  if (item.message && !item.message.includes('Detected') && !item.message.includes('was') && !item.message.includes('event in')) {
    return item.message;
  }

  // Build context-sensitive messages based on operation type and available data
  if (operationType === 'insert') {
    if (userName && documentName) {
      return `${userName} created ${documentName}`;
    }
    if (userName && collectionName) {
      return `${userName} added a new ${collectionName}`;
    }
    if (documentName && collectionName) {
      return `New ${collectionName}: ${documentName}`;
    }
    if (collectionName) {
      return `New ${collectionName} added`;
    }
    return 'New item created';
  }

  if (operationType === 'update') {
    if (userName && documentName) {
      return `${userName} updated ${documentName}`;
    }
    if (userName && collectionName) {
      return `${userName} modified a ${collectionName}`;
    }
    if (documentName && collectionName) {
      return `${documentName} was updated`;
    }
    if (collectionName) {
      return `A ${collectionName} was updated`;
    }
    return 'Item updated';
  }

  if (operationType === 'delete') {
    if (userName && documentName) {
      return `${userName} deleted ${documentName}`;
    }
    if (userName && collectionName) {
      return `${userName} removed a ${collectionName}`;
    }
    if (documentName && collectionName) {
      return `${documentName} was deleted`;
    }
    if (collectionName) {
      return `A ${collectionName} was deleted`;
    }
    return 'Item deleted';
  }

  // For job notifications
  if (item.type?.includes('complete') || item.type?.includes('success')) {
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} completed successfully`;
    }
    return 'Task completed';
  }

  if (item.type?.includes('failed') || item.type?.includes('error')) {
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} failed`;
    }
    return 'Task failed';
  }

  // Fallback to generic message (avoid mentioning collection names)
  if (operationType) {
    const action = operationType === 'insert' ? 'created' : operationType === 'update' ? 'updated' : 'deleted';
    if (documentName) {
      return `${documentName} was ${action}`;
    }
    return `Item was ${action}`;
  }

  return item.body || item.message || 'New notification';
};

const NotificationItem = ({ item, fullView }: {
  item: {
    _id: string,
    body?: string,
    message?: string,
    error?: string,
    warning?: string,
    postedBy?: {
      profile_information: {
        avatar: {
          img: string
        },
        name: string,
        surname: string
      }
    },
    operationType?: string,
    ns?: {
      coll: string
    },
    collection?: string,
    title?: string,
    type?: string,
    stack?: string,
    image?: string,
    screen?: string,
    timestamp?: number | string,
    fullDocument?: any,
    documentKey?: {
      _id?: string
    },
    dataTitle?: string,
    jobName?: string,
    jobId?: string,
    [key: string]: any,
  },
  fullView?: boolean
}) => {
  const theme = themeSelector();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  // Determine notification type for icon/animation
  const notificationType = item.type?.toLowerCase() || '';
  const isError = !!item.error || notificationType.includes('error') || notificationType.includes('failed');
  const isWarning = !!item.warning || notificationType.includes('warning') || notificationType.includes('warn');
  const isSuccess = notificationType.includes('complete') || notificationType.includes('success');

  // Return appropriate icon/animation based on notification type
  const getNotificationIcon = () => {
    // Show user avatar if available (highest priority)
    if (item.postedBy || item.image) {
      return (
        <Animatable.Image
          animation={'slideInLeft'}
          source={{ uri: item.image || item.postedBy.profile_information.avatar.img }}
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            borderWidth: 2,
            borderColor: 'white',
          }}
        />
      );
    }

    // Error notifications
    if (isError) {
      return (
        <LottieView
          autoPlay
          style={{
            width: 40,
            height: 40,
          }}
          source={require('@assets/animations/errorIcon.json')}
          loop={true}
          speed={1}
        />
      );
    }

    // Warning notifications
    if (isWarning) {
      return (
        <LottieView
          autoPlay
          style={{
            width: 40,
            height: 40,
          }}
          source={require('@assets/animations/88006-warning-yellow.json')}
          loop={true}
          speed={1}
        />
      );
    }

    // Success notifications
    if (isSuccess) {
      return (
        <LottieView
          autoPlay
          style={{
            width: 40,
            height: 40,
          }}
          source={require('@assets/animations/registerSuccess.json')}
          loop={false}
        />
      );
    }

    // Default: use returnIcon for operation types
    return returnIcon(item.operationType || item.type);
  };

  return item && (
    <View
      style={[
        getModernCardStyle(theme, 'small'),
        {
          marginVertical: 8,
          marginHorizontal: 4,
          padding: 16,
          top: Platform.select({ native: -15 }),
          flexDirection: 'row',
          alignItems: 'center',
          overflow: 'hidden',
          gap: 12,
          minHeight: 80,
        }
      ]}
    >
      <Pressable
        onPress={() => store.dispatch({ type: SHOW_NOTIFICATIONS_PANEL })}
        style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 }}
      >
        {/* Avatar/Icon Container */}
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 24,
            backgroundColor: isError
              ? (theme ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.2)')
              : isWarning
                ? (theme ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.2)')
                : isSuccess
                  ? (theme ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)')
                  : (theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)'),
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: isError
              ? (theme ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.3)')
              : isWarning
                ? (theme ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.3)')
                : isSuccess
                  ? (theme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.3)')
                  : (theme ? 'rgba(90, 192, 234, 0.2)' : 'rgba(90, 192, 234, 0.3)'),
          }}
        >
          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            {getNotificationIcon()}
          </View>
        </View>

        {/* Content Container */}
        <YStack flex={1} gap={4}>
          {/* Type Label (Error/Warning/Success) */}
          {(isError || isWarning || isSuccess) && (
            <Text
              fontSize={11}
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing={0.5}
              color={
                isError
                  ? '#EF4444'
                  : isWarning
                    ? '#F59E0B'
                    : '#10B981'
              }
            >
              {isError ? 'Error' : isWarning ? 'Warning' : 'Success'}
            </Text>
          )}

          {/* Main Content */}
          <Text
            numberOfLines={2}
            fontWeight="600"
            fontSize={15}
            color={getModernTextColor(theme, 'primary')}
          >
            {getContextualMessage(item)}
          </Text>

          {/* Timestamp */}
          <Text
            fontSize={12}
            color={getModernTextColor(theme, 'muted')}
            fontWeight="500"
          >
            {item.timestamp ? getDaysAgo(typeof item.timestamp === 'string' ? new Date(item.timestamp).getTime() : item.timestamp) : 'Just now'}
          </Text>

          {/* Full view content */}
          {fullView && item.body && (
            <YStack gap={8} marginTop={4}>
              <Text
                fontSize={13}
                lineHeight={18}
                numberOfLines={3}
                color={getModernTextColor(theme, 'secondary')}
              >
                {item.body}
              </Text>
            </YStack>
          )}
        </YStack>

        {/* Action Button */}
        {item.stack && (
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)',
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: theme ? 'rgba(90, 192, 234, 0.3)' : 'rgba(90, 192, 234, 0.4)',
            }}
          >
            <Button
              icon="right"
              iconOnly
              iconSize={16}
              iconColor={theme ? '#5ac0ea' : '#7dd3fc'}
              customStyle={{
                backgroundColor: 'transparent',
                borderWidth: 0,
                padding: 0,
                minWidth: 32,
                minHeight: 32,
              }}
              onPress={() => {
                if (item.stack) {
                  (navigation as any).navigate(item.stack, {
                    screen: item.screen !== 'Upgrade' ? item.screen : undefined,
                    params: {
                      type: item.screen === 'Upgrade' ? 'Upgrade' : undefined,
                      ...(item.integrationName && { integrationName: item.integrationName }),
                    }
                  });
                }
                dispatch({ type: HIDE_NOTIFICATIONS_MODAL });
                setTimeout(() => {
                  dispatch({ type: "UPGRADE_TAB" })
                }, 500)
              }}
            />
          </View>
        )}
      </Pressable>
    </View>
  )
};

export default memo(NotificationItem)